// GQL API for public endpoints
import { MessageContext } from "../../wallets/types";


interface GatewayResponse {
  name: string;
  id: string;
  h3Index: string;
  publicKey: string;
  typeOf: string;
  timeZone?: string;
  ders?: DerInfo[];
}

interface GatewayStatusResponse {
  exists: boolean;
  wallet: boolean;
  location: boolean;
}

// Helper to check if a gateway is hardware type
export const isHardwareGateway = (gateway: GatewayResponse): boolean => {
  return gateway.typeOf === "Hardware";
};

const API_URL = 'https://api.srcful.dev/';
const SOLAR_EDGE_CREATE = "happy-happy-energy";

// Add new interfaces for DER data
export interface DerInfo {
  type: string;
  lastSeen: string;
  sn: string;
  validates?: string;
  derTypeMeta?: {
    name: string;
    dataPoints: string[];
    nominalPower?: number;
  };
}

// Meter data interfaces
export interface MeterData {
  latest: {
    ts: string;
    consumption: number;  // kWh - cumulative energy consumption
    delivery: number;     // kWh - cumulative energy delivered back to grid
    consumptionL1: number;
    consumptionL2: number;
    consumptionL3: number;
    deliveryL1: number;   // kWh - cumulative energy delivered back to grid on phase L1
    deliveryL2: number;   // kWh - cumulative energy delivered back to grid on phase L2
    deliveryL3: number;   // kWh - cumulative energy delivered back to grid on phase L3
  };
  daily: Array<{
    ts: string;         // Timestamp for the daily data
    consumption: number; // kWh - total energy consumption for the day
    delivery: number;    // kWh - total energy delivered for the day
  }>;
  historical: Array<{
    ts: string;
    consumption: number;      // W - current power consumption in Watts
    delivery: number;         // W - current power delivery back to grid in Watts
    deliveryL1: number;       // W - power delivery on phase L1 in Watts
    deliveryL2: number;       // W - power delivery on phase L2 in Watts
    deliveryL3: number;       // W - power delivery on phase L3 in Watts
    consumptionL1: number;    // W - power consumption on phase L1 in Watts
    consumptionL2: number;    // W - power consumption on phase L2 in Watts
    consumptionL3: number;    // W - power consumption on phase L3 in Watts
    totalDelivered: number;   // kWh - total energy delivered
  }>;
}

// Battery data interfaces
export interface BatteryLatest {
  ts: string;
  power: number;  // Current power (positive = charging, negative = discharging)
  soc: number;    // State of charge percentage  
  current: number;
  voltage: number;
}

export interface BatteryHistorical {
  ts: string;
  power: number;
  soc: number;
}

export interface BatteryData {
  latest: BatteryLatest;
  historical: BatteryHistorical[];
}

/**
 * Fetches all gateways associated with a wallet address
 * @param walletAddress - The public key of the wallet as string
 * @returns Promise resolving to array of gateway data
 */
export const fetchWalletGateways = async (walletAddress: string): Promise<GatewayResponse[]> => {
  const query = JSON.stringify({
    query: `
      query get_gateways {
        gateway {
          list(wallet:"${walletAddress}", take: 100, showTestMode: true) {
            h3Index
            id
            publicKey
            name
            typeOf
            timeZone
          }
        }
      }
    `,
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  return json.data.gateway.list;
};

/**
 * Creates a WebSocket subscription for gateway configuration changes
 * @param gatewayId - The ID of the gateway to subscribe to
 * @param message - The signed message for authentication
 * @param signature - The signature for authentication
 * @returns The subscription query string
 */
export const createGatewaySubscriptionQuery = (
  gatewayId: string,
  message: string,
  signature: string
): string => {
  return `
    subscription {
      configurationDataChanges(walletAuth:{
        message: "${message}"
        signature: "${signature}"
        serial: "${gatewayId}"
        subKey: "state"
      }) {
        data
        subKey
      }
    }
  `;
};

/**
 * Fetches DER information for a specific gateway
 * @param gatewayId - The ID of the gateway to fetch DER info for
 * @returns Promise resolving to array of DER information
 */
export const fetchGatewayDers = async (gatewayId: string): Promise<DerInfo[]> => {
  const query = JSON.stringify({
    query: `
      query ders {
        gateway {
          gateway(id:"${gatewayId}") {
            ders {
              type
              lastSeen
              sn
              validates
              derTypeMeta {
                name
                dataPoints
                ... on SolarDerTypeMeta {
                  nominalPower
                }
              }
            }
          }
        }
      }
    `,
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  return json.data.gateway.gateway.ders;
};

/**
 * Creates a new cloud gateway with SolarEdge credentials
 * @param walletAddress - The public key of the wallet as string
 * @param siteId - The SolarEdge site ID
 * @param apiKey - The user's SolarEdge API key
 * @param latitude - The gateway's latitude
 * @param longitude - The gateway's longitude
 * @returns Promise resolving to the created gateway's ID
 */
export const createSolarEdgeCloudGateway = async (
  walletAddress: string,
  siteId: string,
  apiKey: string,
  latitude: string,
  longitude: string
): Promise<string> => {
  const query = JSON.stringify({
    query: `
      mutation {
        gatewayInception {
          registerSolarEdgeApiGateway(
            ownerWallet: "${walletAddress}"
            auth: { apiKey: "${SOLAR_EDGE_CREATE}" }
            longitude: ${parseFloat(longitude)}
            latitude: ${parseFloat(latitude)}
            siteId: "${siteId}"
            solarEdgeApiKey: "${apiKey}"
          ) {
            id
          }
        }
      }
    `
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  return json.data.gatewayInception.registerSolarEdgeApiGateway.id;
};

/**
 * Fetches status information for a specific gateway
 * @param gatewayId - The ID of the gateway to fetch status for
 * @returns Promise resolving to gateway status information
 */
export const fetchGatewayStatus = async (gatewayId: string): Promise<GatewayStatusResponse> => {
  const query = JSON.stringify({
    query: `
      query gw_status {
        gateway {
          status(gwId:"${gatewayId}") {
            exists
            wallet
            location
          }
        }
      }
    `,
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  return json.data.gateway.status;
};

/**
 * Fetches real-time meter data for a specific meter
 * @param serialNumber - The serial number of the meter
 * @returns Promise resolving to meter data including current consumption and phase information
 */
export const fetchMeterData = async (serialNumber: string): Promise<MeterData> => {
  // Calculate UTC timestamps for the last 24 hours
  const now = new Date();
  const stop = now.toISOString();
  const start = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago

  // Calculate dates for daily data (last 30 days)
 

  const query = JSON.stringify({
    query: `
      query energy_meter_data {
        derData {
          energyMeter(sn: "${serialNumber}") {
            latest {
              ts
              consumption
              delivery
              consumptionL1
              consumptionL2
              consumptionL3
              deliveryL1
              deliveryL2
              deliveryL3
            }
            daily {
              ts
              consumption
              delivery
            }
            historical(start: "${start}", stop: "${stop}", resolution: "10m") {
              ts
              consumption
              delivery
              deliveryL1
              deliveryL2
              deliveryL3
              consumptionL1
              consumptionL2
              consumptionL3
              totalDelivered
            }
          }
        }
      }
    `,
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  return json.data.derData.energyMeter;
};

/**
 * Fetches detailed historical meter data for a specific day
 * @param serialNumber - The serial number of the meter
 * @param date - The date to fetch data for (YYYY-MM-DD format or Date object)
 * @param timezone - The timezone to use for day boundaries (default: 'UTC')
 * @returns Promise resolving to detailed historical data for the specific day
 */
export const fetchMeterDataForDay = async (
  serialNumber: string, 
  date: string | Date,
  timezone: string = 'UTC'
): Promise<MeterData['historical']> => {
  // Convert date to Date object if it's a string
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  
  // Calculate start and end of day in the specified timezone
  let startOfDay: Date;
  let endOfDay: Date;
  
  if (timezone === 'UTC') {
    startOfDay = new Date(targetDate);
    startOfDay.setUTCHours(0, 0, 0, 0);
    endOfDay = new Date(targetDate);
    endOfDay.setUTCHours(23, 59, 59, 999);
  } else {
    // For non-UTC timezones, we need to handle timezone offsets
    // This is a simplified approach - for production, consider using a proper timezone library
    startOfDay = new Date(targetDate);
    startOfDay.setHours(0, 0, 0, 0);
    endOfDay = new Date(targetDate);
    endOfDay.setHours(23, 59, 59, 999);
  }

  const start = startOfDay.toISOString();
  const stop = endOfDay.toISOString();

  const query = JSON.stringify({
    query: `
      query energy_meter_day_data {
        derData {
          energyMeter(sn: "${serialNumber}") {
            historical(start: "${start}", stop: "${stop}", resolution: "5m") {
              ts
              consumption
              delivery
              deliveryL1
              deliveryL2
              deliveryL3
              consumptionL1
              consumptionL2
              consumptionL3
              totalDelivered
            }
          }
        }
      }
    `,
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  return json.data.derData.energyMeter.historical || [];
};

/**
 * Fetches detailed historical meter data for a specific day with custom resolution
 * @param serialNumber - The serial number of the meter
 * @param date - The date to fetch data for (YYYY-MM-DD format or Date object)
 * @param resolution - The data resolution (10s, 1m, 5m, 10m, 15m)
 * @param timezone - The timezone to use for day boundaries (default: 'UTC')
 * @returns Promise resolving to detailed historical data for the specific day
 */
export const fetchMeterDataForDayWithResolution = async (
  serialNumber: string, 
  date: string | Date,
  resolution: '10s' | '1m' | '5m' | '10m' | '15m' = '5m',
  timezone: string = 'UTC'
): Promise<MeterData['historical']> => {
  console.log(`Fetching meter data for ${serialNumber} on ${date} with resolution ${resolution}`);
  
  // Convert date to Date object if it's a string
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  
  // Always calculate day boundaries in UTC to avoid timezone issues
  // If the input date is in YYYY-MM-DD format, treat it as local date
  let startOfDay: Date;
  let endOfDay: Date;
  
  if (typeof date === 'string') {
    // For string dates (YYYY-MM-DD), create the date in local timezone
    // then convert to proper UTC boundaries
    const dateParts = date.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2]);
    
    startOfDay = new Date(year, month, day, 0, 0, 0, 0);
    endOfDay = new Date(year, month, day, 23, 59, 59, 999);
  } else {
    // For Date objects, use the date as-is
    startOfDay = new Date(targetDate);
    startOfDay.setHours(0, 0, 0, 0);
    endOfDay = new Date(targetDate);
    endOfDay.setHours(23, 59, 59, 999);
  }

  const start = startOfDay.toISOString();
  const stop = endOfDay.toISOString();

  console.log(`API query: start=${start}, stop=${stop}, resolution=${resolution}`);

  const query = JSON.stringify({
    query: `
      query energy_meter_day_data_with_resolution {
        derData {
          energyMeter(sn: "${serialNumber}") {
            historical(start: "${start}", stop: "${stop}", resolution: "${resolution}") {
              ts
              consumption
              delivery
              deliveryL1
              deliveryL2
              deliveryL3
              consumptionL1
              consumptionL2
              consumptionL3
              totalDelivered
            }
          }
        }
      }
    `,
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    console.error('API errors:', json.errors);
    throw new Error(json.errors[0].message);
  }

  const result = json.data.derData.energyMeter.historical || [];
  console.log(`Received ${result.length} data points for resolution ${resolution}`);
  
  // Debug: Log first few and last few timestamps to verify resolution
  if (result.length > 0) {
    console.log(`🔍 [API DEBUG] First 5 timestamps:`);
    result.slice(0, 5).forEach((point: any, index: number) => {
      const timestamp = new Date(point.ts);
      console.log(`  ${index + 1}: ${timestamp.toISOString()} (${point.consumption}W)`);
    });
    
    if (result.length > 10) {
      console.log(`🔍 [API DEBUG] Last 5 timestamps:`);
      result.slice(-5).forEach((point: any, index: number) => {
        const timestamp = new Date(point.ts);
        console.log(`  ${result.length - 4 + index}: ${timestamp.toISOString()} (${point.consumption}W)`);
      });
    }
    
    // Check interval between first two points
    if (result.length > 1) {
      const firstTime = new Date(result[0].ts).getTime();
      const secondTime = new Date(result[1].ts).getTime();
      const intervalMinutes = (secondTime - firstTime) / (1000 * 60);
      console.log(`🔍 [API DEBUG] Interval between first two points: ${intervalMinutes} minutes`);
    }
  }
  
  return result;
};

/**
 * Fetches daily meter data with custom date range
 * @param serialNumber - The serial number of the meter
 * @param startDate - The start date (YYYY-MM-DD format or Date object)
 * @param endDate - The end date (YYYY-MM-DD format or Date object)
 * @returns Promise resolving to daily meter data for the specified date range
 */
export const fetchMeterDailyDataRange = async (
  serialNumber: string,
  startDate: string | Date,
  endDate: string | Date
): Promise<MeterData['daily']> => {
  // Convert dates to YYYY-MM-DD format
  const startDateStr = typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0];
  const endDateStr = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];

  console.log(`🚀 [RAW API] Fetching daily range data ${serialNumber} from ${startDate} to ${endDate}`);


  const query = JSON.stringify({
    query: `
      query energy_meter_daily_range {
        derData {
          energyMeter(sn: "${serialNumber}") {
            daily(start: "${startDateStr}", stop: "${endDateStr}") {
              ts
              consumption
              delivery
            }
          }
        }
      }
    `,
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  return json.data.derData.energyMeter.daily || [];
};

/**
 * Fetches raw historical meter data using UTC timestamps
 * @param serialNumber - The serial number of the meter
 * @param startTime - Start time in UTC (ISO string)
 * @param endTime - End time in UTC (ISO string)
 * @param resolution - The data resolution (10s, 1m, 5m, 10m, 15m)
 * @returns Promise resolving to historical data for the specified time range
 */
export const fetchMeterHistoricalData = async (
  authContext: MessageContext,
  serialNumber: string, 
  startTime: string,
  endTime: string,
  resolution: '10s' | '1m' | '5m' | '10m' | '15m' = '5m'
): Promise<MeterData['historical']> => {
  console.log(`🚀 [RAW API] Fetching meter data for ${serialNumber} from ${startTime} to ${endTime} with resolution ${resolution}`);

  // Determine authentication method
  const hasApiKey = !!authContext.apiKey;
  const hasSignedMessage = !!authContext.signedMessage?.message && !!authContext.signedMessage?.signature;

  if (!hasApiKey && !hasSignedMessage) {
    throw new Error('Authentication required: either apiKey or signedMessage must be provided');
  }

  // Build the authentication parameters based on the available method
  let authParams = '';
  
  if (hasApiKey) {
    // Use API key authentication
    authParams = `apiKey: "${authContext.apiKey}"`;
    console.log(`🔑 [RAW API] Using API key authentication`);
  } else {
    // Use wallet-based authentication
    authParams = `walletAuth: {
      message: "${authContext.signedMessage!.message}"
      signature: "${authContext.signedMessage!.signature}"
      subKey: ""
      ${authContext.authToken ? `delegationToken: "${authContext.authToken}"` : ''}
    }`;
    console.log(`👛 [RAW API] Using wallet-based authentication`);
  }

  const query = JSON.stringify({
    query: `
      query energy_meter_raw_historical {
        derData {
            energyMeter: authorizedEnergyMeter(${authParams}, sn: "${serialNumber}") {
            historical(start: "${startTime}", stop: "${endTime}", resolution: "${resolution}") {
              ts
              consumption
              delivery
              deliveryL1
              deliveryL2
              deliveryL3
              consumptionL1
              consumptionL2
              consumptionL3
              totalDelivered
            }
          }
        }
      }
    `,
  });

  console.log(`🔑 [RAW API] Query: ${query}`);

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    console.error('🚨 [RAW API] API errors:', json.errors);
    throw new Error(json.errors[0].message);
  }

  const result = json.data.derData.energyMeter.historical || [];
  console.log(`✅ [RAW API] Received ${result.length} data points for ${resolution} resolution`);
  
  return result;
};

/**
 * Fetches battery data for a specific serial number
 * @param serialNumber - Battery serial number
 * @param startDate - Start date for historical data (ISO string)
 * @param stopDate - Stop date for historical data (ISO string)
 * @param resolution - Data resolution (e.g., "24h", "1h", "5m")
 * @returns Promise resolving to battery data
 */
export const fetchBatteryData = async (
  serialNumber: string,
  startDate: string = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  stopDate: string = new Date().toISOString(),
  resolution: string = "24h"
): Promise<BatteryData> => {
  const query = JSON.stringify({
    query: `
      query battery_data {
        derData {
          battery(sn:"${serialNumber}") {
            latest {
              ts
              power
              soc
              current
              voltage
            }
            historical(start:"${startDate}", stop:"${stopDate}", resolution: "${resolution}") {
              ts
              power
              soc
            }
          }
        }
      }
    `
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  const batteryData = json.data?.derData?.battery;
  if (!batteryData) {
    throw new Error('No battery data found');
  }

  return {
    latest: {
      ts: batteryData.latest?.ts || '',
      power: batteryData.latest?.power || 0,
      soc: batteryData.latest?.soc || 0,
      current: batteryData.latest?.current || 0,
      voltage: batteryData.latest?.voltage || 0
    },
    historical: (batteryData.historical || []).map((item: any) => ({
      ts: item.ts,
      power: item.power || 0,
      soc: item.soc || 0
    }))
  };
};

/**
 * Fetches intraday battery data with higher resolution for a specific day
 * @param serialNumber - Battery serial number
 * @param startDate - Start date for the day (ISO string)
 * @param stopDate - Stop date for the day (ISO string) 
 * @param resolution - Data resolution (e.g., "5m", "15m", "1h")
 * @returns Promise resolving to intraday battery data
 */
export const fetchBatteryIntradayData = async (
  serialNumber: string,
  startDate: string,
  stopDate: string,
  resolution: string = "15m"
): Promise<BatteryHistorical[]> => {
  const query = JSON.stringify({
    query: `
      query battery_intraday_data {
        derData {
          battery(sn:"${serialNumber}") {
            historical(start:"${startDate}", stop:"${stopDate}", resolution: "${resolution}") {
              ts
              power
              soc
            }
          }
        }
      }
    `
  });

  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json');
  requestHeaders.set('Content-Length', query.length.toString());

  const response = await fetch(API_URL, {
    method: 'POST',
    body: query,
    headers: requestHeaders,
  });

  const json = await response.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }

  const batteryData = json.data?.derData?.battery;
  if (!batteryData) {
    return [];
  }

  return (batteryData.historical || []).map((item: any) => ({
    ts: item.ts,
    power: item.power || 0,
    soc: item.soc || 0
  }));
};
